import { useAnimation } from "framer-motion"
import { useCallback, useEffect, useState } from "react"

export function useLogoAnimation() {
  const greenControls = useAnimation()
  const redControls = useAnimation()
  const [tradeActive, setTradeActive] = useState(false)

  // Helper to execute one quick button press animation
  const pressOnce = useCallback((controls: ReturnType<typeof useAnimation>) => {
    controls.start({
      scale: [1, 0.8, 1.03, 1],
      opacity: [1, 0.7, 1, 1],
      transition: { duration: 0.25, ease: "easeInOut" },
    })
  }, [])

  // Listen for tradeActivity → random buy/sell mash
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ active: boolean }>
      setTradeActive(!!ev.detail?.active)
    }

    window.addEventListener("tradeActivity", handler)
    return () => window.removeEventListener("tradeActivity", handler)
  }, [])

  // Handle trade activity animation loop
  useEffect(() => {
    if (!tradeActive) {
      // Reset & stop any running sequences once trade is inactive
      greenControls.stop()
      redControls.stop()
      greenControls.set({ scale: 1, opacity: 1 })
      redControls.set({ scale: 1, opacity: 1 })
      return
    }

    let cancelled = false
    let current: "green" | "red" = Math.random() < 0.5 ? "green" : "red"
    let remaining = 2 + Math.floor(Math.random() * 3) // 2–4 presses

    const loop = () => {
      if (cancelled || !tradeActive) return

      const controls = current === "green" ? greenControls : redControls
      pressOnce(controls)

      remaining -= 1
      if (remaining <= 0) {
        current = current === "green" ? "red" : "green"
        remaining = 1 + Math.floor(Math.random() * 3) // 1–3 presses next side
      }

      const delay = 200 + Math.random() * 150 // 0.2–0.35s between presses
      setTimeout(loop, delay)
    }

    loop()

    // Automatically stop the mash after ~3 s
    const durationTimer = setTimeout(() => {
      cancelled = true
      greenControls.stop()
      redControls.stop()
      greenControls.set({ scale: 1, opacity: 1 })
      redControls.set({ scale: 1, opacity: 1 })
    }, 3125)

    return () => {
      cancelled = true
      clearTimeout(durationTimer)
    }
  }, [tradeActive, greenControls, redControls, pressOnce])

  // Single press for each platform advance (only green caret)
  useEffect(() => {
    const handler = () => {
      if (tradeActive) return // Ignore if mash is active
      pressOnce(greenControls)
    }

    window.addEventListener("platformAdvance", handler)
    return () => window.removeEventListener("platformAdvance", handler)
  }, [tradeActive, greenControls, pressOnce])

  return {
    greenControls,
    redControls,
    tradeActive,
  }
}
